// Point weights: per point, how much its frame's photo counts where the photos are blended (Image weights
// shading, mode 10: each pixel the weighted mean of the photos there). 1 unless a Set weight or Blur weights step
// (macros.ts) changed it; saved with the scan, per point id (scans/:id/weights on the editor server). The blur and
// the shading's per-frame grids work on each frame's depth image: a point's pixel is where it lands in its frame's
// depth camera, placed as the editor draws the frame. Pure.
import { type Rigid, inverse } from '../align.ts';

export type WeightOp = 'set' | 'add' | 'sub' | 'mul';

/** Weights of n points, all 1. */
export const newWeights = (n: number) => new Float32Array(n).fill(1);

/** w op x on the points of mask (kept at 0 or more; x per point, or the same for all): how many it changed. */
export function applyOp(w: Float32Array, mask: Uint8Array, op: WeightOp, xs: number | ((i: number) => number)): number {
  let n = 0;
  for (let i = 0; i < w.length; i++) {
    if (!mask[i]) continue;
    const x = typeof xs === 'number' ? xs : xs(i), a = w[i], b = op === 'set' ? x : op === 'add' ? a + x : op === 'sub' ? a - x : a * x;
    w[i] = Math.max(0, b);
    if (w[i] !== a) n++;
  }
  return n;
}

/** A frame's depth image: depth in metres (dw x dh, row-major, 0 = no reading), its intrinsics (fx, fy, cx, cy) and where its camera is (camera to world). */
export interface DepthFrame { depth: Float32Array; dw: number; dh: number; K: number[]; place: Rigid }

/** The points of each frame (by frame id; 0, no frame, left out). */
export function byFrame(frame: Uint32Array, keep?: (i: number) => boolean): Map<number, number[]> {
  const of = new Map<number, number[]>();
  for (let i = 0; i < frame.length; i++) {
    const f = frame[i];
    if (!f || (keep && !keep(i))) continue;
    let a = of.get(f);
    if (!a) of.set(f, (a = []));
    a.push(i);
  }
  return of;
}

/** Each of the points idx (world P): its pixel in the frame's depth image (y * dw + x), -1 when off it or behind the camera. */
export function pixelsOf(P: Float32Array, idx: number[], fr: DepthFrame): Int32Array {
  const out = new Int32Array(idx.length).fill(-1), Pi = inverse(fr.place), R = Pi.R, t = Pi.t, K = fr.K;
  idx.forEach((i, j) => {
    const x = P[3 * i], y = P[3 * i + 1], z = P[3 * i + 2];
    const qx = R[0] * x + R[1] * y + R[2] * z + t[0], qy = R[3] * x + R[4] * y + R[5] * z + t[1], qz = R[6] * x + R[7] * y + R[8] * z + t[2];
    if (qz <= 1e-6) return;
    const u = Math.round(K[0] * qx / qz + K[2]), v = Math.round(K[1] * qy / qz + K[3]);
    if (u >= 0 && v >= 0 && u < fr.dw && v < fr.dh) out[j] = v * fr.dw + u;
  });
  return out;
}

/** The mean weight of the points in each pixel with a depth reading, and how many there are (0: none, the mean 0). */
function splat(w: ArrayLike<number>, idx: number[], pix: Int32Array, fr: DepthFrame, use?: (i: number) => boolean) {
  const sum = new Float32Array(fr.dw * fr.dh), cnt = new Uint32Array(fr.dw * fr.dh);
  idx.forEach((i, j) => { const p = pix[j]; if (p >= 0 && fr.depth[p] > 0 && (!use || use(i))) { sum[p] += w[i]; cnt[p]++; } });
  for (let p = 0; p < sum.length; p++) if (cnt[p]) sum[p] /= cnt[p];
  return { mean: sum, cnt };
}

/** v convolved along one axis with kernel g (radius g.length - 1), in place of out. */
function pass(v: Float32Array, out: Float32Array, w: number, h: number, g: Float32Array, alongX: boolean) {
  const r = g.length - 1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let s = 0;
    for (let k = -r; k <= r; k++) {
      const xx = alongX ? x + k : x, yy = alongX ? y : y + k;
      if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
      s += g[Math.abs(k)] * v[yy * w + xx];
    }
    out[y * w + x] = s;
  }
}

/**
 * Blur the weights of the points idx (of one frame; pix: their pixels) across the frame's depth image, Gaussian
 * (sigma r / 2, out to r pixels): only the points `change` says are changed, each taking its pixel's blurred
 * value; every one of them with a pixel counts. Pixels with no depth reading, or no point, neither count nor
 * take a value (the average is over those that do). How many weights it changed.
 */
export function blurFrame(w: Float32Array, idx: number[], pix: Int32Array, fr: DepthFrame, r: number, change: (i: number) => boolean): number {
  const { dw, dh } = fr, { mean, cnt } = splat(w, idx, pix, fr), R = Math.max(1, Math.round(r)), sd = Math.max(0.5, r / 2);
  const g = Float32Array.from({ length: R + 1 }, (_, k) => Math.exp(-(k * k) / (2 * sd * sd)));
  const val = new Float32Array(dw * dh), has = new Float32Array(dw * dh), t = new Float32Array(dw * dh);
  for (let p = 0; p < val.length; p++) if (cnt[p]) { val[p] = mean[p]; has[p] = 1; }
  // normalised convolution: blur the values and where there are any, then divide
  pass(val, t, dw, dh, g, true); pass(t, val, dw, dh, g, false);
  pass(has, t, dw, dh, g, true); pass(t, has, dw, dh, g, false);
  let n = 0;
  idx.forEach((i, j) => {
    const p = pix[j];
    if (p < 0 || !cnt[p] || !change(i) || !(has[p] > 0)) return;
    const b = Math.max(0, val[p] / has[p]);
    if (b !== w[i]) { w[i] = b; n++; }
  });
  return n;
}

/**
 * A frame's weights as a grid over its depth image (for the shading): each pixel with a depth reading the mean
 * weight of the points in it (those `use` takes), else of those within 2 pixels (the depth image is denser than
 * the points), else 1; 0 where there is no depth (no photo drawn there).
 */
export const frameWeightGrid = (w: Float32Array, idx: number[], pix: Int32Array, fr: DepthFrame, use?: (i: number) => boolean) => frameGrid(w, idx, pix, fr, 1, use);

/** The same for a selection (mask: 0 or 1 per point): how much of each pixel's points are selected, 0 where none are near. */
export const frameSelGrid = (mask: Uint8Array, idx: number[], pix: Int32Array, fr: DepthFrame) => frameGrid(mask, idx, pix, fr, 0);

function frameGrid(v: ArrayLike<number>, idx: number[], pix: Int32Array, fr: DepthFrame, dflt: number, use?: (i: number) => boolean): Float32Array {
  const { dw, dh } = fr, { mean, cnt } = splat(v, idx, pix, fr, use), out = new Float32Array(dw * dh);
  for (let y = 0; y < dh; y++) for (let x = 0; x < dw; x++) {
    const p = y * dw + x;
    if (!(fr.depth[p] > 0)) continue;
    if (cnt[p]) { out[p] = mean[p]; continue; }
    let s = 0, k = 0;
    for (let yy = Math.max(0, y - 2); yy <= Math.min(dh - 1, y + 2); yy++) for (let xx = Math.max(0, x - 2); xx <= Math.min(dw - 1, x + 2); xx++) {
      const q = yy * dw + xx;
      if (cnt[q]) { s += mean[q]; k++; }
    }
    out[p] = k ? s / k : dflt;
  }
  return out;
}

/** Weights per vertex from a saved file (per point id; null: none saved, all 1). */
export function weightsOfPids(saved: Float32Array | null, pidOfV: Uint32Array): Float32Array {
  const w = newWeights(pidOfV.length);
  if (saved) for (let v = 0; v < w.length; v++) { const p = pidOfV[v]; if (p < saved.length && Number.isFinite(saved[p])) w[v] = saved[p]; }
  return w;
}

/** The file to save: per point id (npid of them), each vertex's weight; ids not built keep what was saved (or 1). */
export function pidsOfWeights(w: Float32Array, pidOfV: Uint32Array, npid: number, saved: Float32Array | null): Float32Array {
  const out = newWeights(npid);
  if (saved) out.set(saved.subarray(0, Math.min(npid, saved.length)));
  for (let v = 0; v < w.length; v++) out[pidOfV[v]] = w[v];
  return out;
}
